import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatINR } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Calendar,
  Users,
  TrendingUp,
  Clock,
  ArrowRight,
  Stethoscope,
  MessageCircle,
  Loader2,
  PhoneCall,
  PlayCircle,
  Hash,
  ListOrdered,
  UserPlus,
  Receipt,
  IndianRupee,
} from "lucide-react";
import { toast } from "sonner";
import { openWhatsApp, TEMPLATES, formatAppointmentDateTime } from "../lib/whatsapp";

function QuickAction({ to, state, icon: Icon, label, color, testid }) {
  return (
    <Link
      to={to}
      state={state}
      className={`flex items-center gap-2 px-3 py-2.5 rounded-lg font-medium text-sm transition-all ${color}`}
      data-testid={testid}
    >
      <Icon className="w-4 h-4 flex-shrink-0" strokeWidth={2} />
      <span className="truncate">{label}</span>
    </Link>
  );
}

function StatCard({ icon: Icon, label, value, sub, accent = "blue", testid }) {  const accents = {
    blue: "bg-blue-50 text-blue-600",
    emerald: "bg-emerald-50 text-emerald-600",
    sky: "bg-sky-50 text-sky-600",
    amber: "bg-amber-50 text-amber-600",
  };
  return (
    <Card className="card-hover border-slate-200" data-testid={testid}>
      <CardContent className="p-6">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${accents[accent]} mb-4`}>
          <Icon className="w-5 h-5" strokeWidth={1.75} />
        </div>
        <div className="text-3xl font-bold text-slate-900" style={{ fontFamily: "Manrope" }}>
          {value}
        </div>
        <div className="text-sm text-slate-500 mt-1">{label}</div>
        {sub && <div className="text-xs text-slate-400 mt-1">{sub}</div>}
      </CardContent>
    </Card>
  );
}

const statusColors = {
  scheduled: "bg-blue-50 text-blue-700 border-blue-200",
  completed: "bg-emerald-50 text-emerald-700 border-emerald-200",
  cancelled: "bg-red-50 text-red-700 border-red-200",
  no_show: "bg-amber-50 text-amber-700 border-amber-200",
};

function formatTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true });
}

export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [queue, setQueue] = useState(null);
  const [loading, setLoading] = useState(true);
  const [clinicName, setClinicName] = useState("our clinic");

  useEffect(() => {
    api.get("/clinic/settings").then(({ data }) => setClinicName(data?.name || "our clinic")).catch(() => {});
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/dashboard/summary");
      setData(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const loadQueue = async () => {
    try {
      const params = user?.role === "doctor" ? { doctor_id: user.user_id } : {};
      const { data } = await api.get("/queue/today", { params });
      setQueue(data);
    } catch (e) { /* ignore */ }
  };

  useEffect(() => {
    load();
    loadQueue();
    // eslint-disable-next-line
  }, []);

  const sendWhatsApp = (apt) => {
    if (!apt.patient_phone) {
      toast.error("Patient phone is missing");
      return;
    }
    const fmt = formatAppointmentDateTime(apt.date_time);
    const msg = TEMPLATES.appointmentReminder({
      patient_name: apt.patient_name,
      clinic_name: clinicName,
      appointment_date: fmt.date,
      appointment_time: fmt.time,
    });
    openWhatsApp(apt.patient_phone, msg);
  };

  if (loading || !data) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-10 max-w-7xl mx-auto fade-in" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex items-start justify-between mb-8">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-blue-600 mb-2">
            Dashboard
          </div>
          <h1 className="text-3xl lg:text-4xl font-bold text-slate-900" style={{ fontFamily: "Manrope" }}>
            Good day, {(() => {
              const name = user?.name || "Doctor";
              const parts = name.split(" ").filter(Boolean);
              const titles = ["Dr.", "Dr", "Mr.", "Ms.", "Mrs."];
              return titles.includes(parts[0]) && parts[1] ? parts[1] : parts[0];
            })()}.
          </h1>
          <p className="text-slate-600 mt-1">Here's what's happening at your clinic today.</p>
        </div>
        <Link to="/appointments">
          <Button className="bg-blue-600 hover:bg-blue-700 hidden sm:inline-flex" data-testid="dashboard-book-btn">
            <Calendar className="w-4 h-4 mr-2" />
            New appointment
          </Button>
        </Link>
      </div>

      {/* Quick actions bar */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2 mb-8" data-testid="dashboard-quick-actions">
        <QuickAction
          to="/patients"
          state={{ openNewPatient: true }}
          icon={UserPlus}
          label="New patient"
          color="bg-blue-50 text-blue-700 hover:bg-blue-100"
          testid="quick-new-patient"
        />
        <QuickAction
          to="/appointments"
          state={{ openWalkIn: true }}
          icon={Users}
          label="Walk-in"
          color="bg-purple-50 text-purple-700 hover:bg-purple-100"
          testid="quick-walkin"
        />
        <QuickAction
          to="/appointments"
          state={{ openBook: true }}
          icon={Calendar}
          label="New appointment"
          color="bg-sky-50 text-sky-700 hover:bg-sky-100"
          testid="quick-new-appt"
        />
        <QuickAction
          to="/billing"
          icon={Receipt}
          label="Generate bill"
          color="bg-amber-50 text-amber-700 hover:bg-amber-100"
          testid="quick-generate-bill"
        />
        <QuickAction
          to="/billing"
          icon={IndianRupee}
          label="Mark paid"
          color="bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
          testid="quick-mark-paid"
        />
        <QuickAction
          to="/queue"
          icon={ListOrdered}
          label="Today's queue"
          color="bg-slate-100 text-slate-700 hover:bg-slate-200"
          testid="quick-queue"
        />
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <StatCard
          icon={Calendar}
          label="Today's appointments"
          value={data.today_appointments_count}
          accent="blue"
          testid="stat-today-appts"
        />
        <StatCard
          icon={Users}
          label="Total patients"
          value={data.total_patients}
          accent="sky"
          testid="stat-total-patients"
        />
        <StatCard
          icon={TrendingUp}
          label="Total revenue"
          value={formatINR(data.revenue_total)}
          sub="From paid invoices"
          accent="emerald"
          testid="stat-revenue"
        />
        <StatCard
          icon={Clock}
          label="Upcoming"
          value={data.upcoming_appointments.length}
          sub="In next days"
          accent="amber"
          testid="stat-upcoming"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Today's queue widget */}
        {queue && (queue.now_serving || (queue.counts?.waiting || 0) > 0) && (
          <Card className="lg:col-span-3 border-blue-200 bg-gradient-to-r from-blue-50 to-white" data-testid="queue-widget">
            <CardContent className="p-5">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center gap-4 flex-wrap">
                  <div className="w-12 h-12 rounded-full bg-blue-600 text-white flex items-center justify-center">
                    <PlayCircle className="w-6 h-6" />
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-[0.18em] font-semibold text-blue-600">Now serving</div>
                    {queue.now_serving ? (
                      <div className="text-xl font-bold text-slate-900 flex items-center gap-2 flex-wrap" style={{ fontFamily: "Manrope" }}>
                        {queue.now_serving.patient_name}
                        {queue.now_serving.type === "walk_in" && (
                          <Badge variant="outline" className="bg-purple-50 border-purple-200 text-purple-700 gap-1 text-xs">
                            <Hash className="w-3 h-3" /> #{queue.now_serving.queue_number}
                          </Badge>
                        )}
                      </div>
                    ) : (
                      <div className="text-lg font-semibold text-slate-700">Queue ready</div>
                    )}
                  </div>
                  <div className="ml-2 flex items-center gap-4 text-sm">
                    <div className="flex items-center gap-1.5">
                      <ListOrdered className="w-4 h-4 text-blue-500" />
                      <span className="font-semibold text-slate-900" data-testid="dashboard-waiting-count">{queue.counts?.waiting || 0}</span>
                      <span className="text-slate-500">waiting</span>
                    </div>
                    <div className="hidden sm:flex items-center gap-1.5">
                      <span className="font-semibold text-slate-900">{queue.counts?.done || 0}</span>
                      <span className="text-slate-500">done</span>
                    </div>
                  </div>
                </div>
                <Link to="/queue">
                  <Button className="bg-blue-600 hover:bg-blue-700" data-testid="open-queue-btn">
                    <PhoneCall className="w-4 h-4 mr-2" /> Open queue
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Today's appointments */}
        <Card className="lg:col-span-2 border-slate-200" data-testid="today-appointments-card">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
            <CardTitle className="text-lg font-semibold" style={{ fontFamily: "Manrope" }}>
              Today's schedule
            </CardTitle>
            <Link to="/appointments" className="text-sm text-blue-600 font-medium hover:underline flex items-center gap-1">
              View all <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </CardHeader>
          <CardContent>
            {data.today_appointments.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <Calendar className="w-10 h-10 mx-auto mb-3 text-slate-300" />
                <p className="text-sm">No appointments scheduled today.</p>
              </div>
            ) : (
              <div className="space-y-2">
                {data.today_appointments.map((apt) => (
                  <div
                    key={apt.appointment_id}
                    className="flex items-center gap-4 p-4 rounded-lg border border-slate-200 hover:border-blue-200 hover:bg-blue-50/30 transition-all"
                    data-testid={`today-appt-${apt.appointment_id}`}
                  >
                    <div className="w-14 text-center">
                      <div className="text-sm font-semibold text-slate-900">{formatTime(apt.date_time)}</div>
                    </div>
                    <div className="w-px h-10 bg-slate-200" />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-slate-900 truncate">{apt.patient_name}</div>
                      <div className="text-xs text-slate-500 truncate">{apt.reason || "Consultation"}</div>
                    </div>
                    <Badge variant="outline" className={statusColors[apt.status]}>
                      {apt.status.replace("_", " ")}
                    </Badge>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => sendWhatsApp(apt)}
                      data-testid={`whatsapp-btn-${apt.appointment_id}`}
                      className="text-slate-500 hover:text-green-600 hover:bg-green-50"
                      title="Send WhatsApp reminder"
                    >
                      <MessageCircle className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent patients */}
        <Card className="border-slate-200" data-testid="recent-patients-card">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
            <CardTitle className="text-lg font-semibold" style={{ fontFamily: "Manrope" }}>
              Recent patients
            </CardTitle>
            <Link to="/patients" className="text-sm text-blue-600 font-medium hover:underline flex items-center gap-1">
              View all <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </CardHeader>
          <CardContent>
            {data.recent_patients.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <Users className="w-10 h-10 mx-auto mb-3 text-slate-300" />
                <p className="text-sm">No patients yet.</p>
              </div>
            ) : (
              <div className="space-y-1">
                {data.recent_patients.map((p) => (
                  <Link
                    key={p.patient_id}
                    to={`/patients/${p.patient_id}`}
                    className="flex items-center gap-3 p-2.5 rounded-lg hover:bg-slate-50 transition-colors"
                    data-testid={`recent-patient-${p.patient_id}`}
                  >
                    <div className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-500 to-sky-400 text-white flex items-center justify-center text-sm font-semibold">
                      {p.name.charAt(0)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm text-slate-900 truncate">{p.name}</div>
                      <div className="text-xs text-slate-500 truncate">
                        {p.age}y · {p.gender} · {p.phone}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
